import { type AppResourceHint, validAppResource } from './app-resource.js';
import { validAppPath } from './app-route.js';
import {
  hostState,
  readHostColorScheme,
  type HostColorScheme,
  readRoster,
  readViewerLabel,
  record,
  refusal,
  trustedOrigin,
  type ViewerLabel,
  type HostProvider,
} from './host-state.js';

type Pending = {
  resolve(value: string): void;
  reject(error: Error): void;
  n?: number;
  retries: number;
  timer: ReturnType<typeof setTimeout>;
  retry?: ReturnType<typeof setTimeout>;
};
const positive = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) > 0;
const initId = (value: unknown) => typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value);

/** One instance per document. Never instantiate from a React effect. */
export function embeddedHost(
  win: Window,
  parentOrigin: string,
  allowlist: readonly string[],
): HostProvider {
  const state = hostState();
  const origin = trustedOrigin(parentOrigin);
  const trusted = origin !== null && allowlist.includes(origin) && win.parent !== win;
  let generation: number | undefined;
  let colorScheme: HostColorScheme | undefined;
  function acceptTheme(value: unknown) {
    const next = readHostColorScheme(value);
    if (next === undefined || next === colorScheme) return;
    colorScheme = next;
    state.set(state.getState());
  }
  let initialSeen = false;
  /** Wire v1.2: who the host says the viewer is, from the FIRST accepted INIT only (a REFRESH never carries it). */
  let viewerLabel: ViewerLabel | undefined;
  /** CONTRATO-TASKS-MIEMBROS-v1: the signed members statement that came with the LATEST grant (INIT or REFRESH); opaque here. */
  let roster: string | undefined;
  let initialGrant: string | undefined;
  /** A refusal that arrived BEFORE the first `open` asked: the first `open` is rejected with it (once), never answered with a REFRESH. */
  let initialRefusal: ReturnType<typeof refusal> | undefined;
  let n = 0;
  let pending: Pending | undefined;
  let readyTimers: ReturnType<typeof setTimeout>[] = [];
  function stopReady() {
    for (const timer of readyTimers) clearTimeout(timer);
    readyTimers = [];
  }
  function post(type: string, payload?: object) {
    if (trusted)
      win.parent.postMessage(
        { type, __bridge: 'v1', timestamp: Date.now(), ...(payload ? { payload } : {}) },
        origin,
      );
  }
  function finish(grant?: string, reason = 'unavailable') {
    const current = pending;
    pending = undefined;
    if (current) {
      clearTimeout(current.timer);
      clearTimeout(current.retry);
    }
    stopReady();
    if (grant) {
      state.set('ready');
      current?.resolve(grant);
    } else {
      state.set(reason === 'timeout' ? 'timeout' : `refused:${refusal(reason)}`);
      current?.reject(new Error(reason));
    }
  }
  function ready() {
    stopReady();
    post('AI_APP_READY');
    const delays = [100, 300, 1000, 3000, 8000, 13000];
    for (const delay of delays)
      readyTimers.push(
        setTimeout(() => {
          post('AI_APP_READY');
          // the schedule is spent with the last retry: a later `open` (a person's retry) may start another one
          if (delay === delays[delays.length - 1]) readyTimers = [];
        }, delay),
      );
  }
  if (trusted)
    win.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (event.source !== win.parent || event.origin !== origin || !record(event.data)) return;
      const message = event.data;
      if (
        message.__bridge !== 'v1' ||
        typeof message.timestamp !== 'number' ||
        !Number.isFinite(message.timestamp) ||
        !record(message.payload)
      )
        return;
      const p = message.payload;
      if (!positive(p.generation) || (generation !== undefined && p.generation !== generation))
        return;
      if (message.type === 'AI_HOST_THEME') {
        if (generation !== undefined) acceptTheme(p.colorScheme);
        return;
      }
      if (
        message.type === 'AI_AUTH_INIT' ||
        (message.type === 'AI_AUTH_REFUSED' && initId(p.initId) && p.requestId === undefined)
      ) {
        if (initialSeen || !initId(p.initId)) return;
        if (
          message.type === 'AI_AUTH_INIT' &&
          (typeof p.grant !== 'string' || !p.grant || 'accessToken' in p)
        )
          return;
        generation = p.generation;
        initialSeen = true;
        if (message.type === 'AI_AUTH_INIT') {
          acceptTheme(p.colorScheme);
          viewerLabel = readViewerLabel(p);
          roster = readRoster(p);
        }
        stopReady();
        if (message.type === 'AI_AUTH_REFUSED') {
          if (!(pending && pending.n === undefined)) initialRefusal = refusal(p.reason);
          finish(undefined, refusal(p.reason));
          return;
        }
        const grant = p.grant as string;
        if (pending && pending.n === undefined) finish(grant);
        else {
          initialGrant = grant;
          state.set('ready');
        }
        return;
      }
      if (
        !pending ||
        pending.n === undefined ||
        p.requestId !== pending.n ||
        generation === undefined
      )
        return;
      if (message.type === 'AI_AUTH_REFRESH') {
        if (typeof p.grant === 'string' && p.grant && !('accessToken' in p)) {
          roster = readRoster(p);
          finish(p.grant);
        }
      } else if (message.type === 'AI_AUTH_REFUSED') {
        const reason = refusal(p.reason);
        if (reason === 'busy' && pending.retries < 3) {
          if (pending.retry) return;
          pending.retries++;
          const current = pending;
          current.retry = setTimeout(() => {
            current.retry = undefined;
            if (pending === current)
              post('AI_AUTH_REQUEST_REFRESH', { requestId: current.n, generation });
          }, 500);
        } else finish(undefined, reason);
      }
    });
  // CONTRATO-1b2-WIRE §2.2: READY goes out once the listener is installed (not on the first `grants`), and is retried until INIT/REFUSED.
  // An answer that comes before the first `open` is kept for it: an INIT in `initialGrant`, a refusal in `initialRefusal`.
  if (trusted) ready();
  return {
    getState: state.getState,
    subscribe: state.subscribe,
    viewerLabel: () => viewerLabel,
    colorScheme: () => colorScheme,
    roster: () => roster,
    reportRoute(path, resource?: AppResourceHint) {
      // only after the first accepted INIT (that is when the host's generation is known) and only a valid path; the resource (what is open,
      // by reference) rides only when it is valid — an invalid one costs only itself, never the path (APP-CONTEXT-DESK-1)
      if (generation === undefined || validAppPath(path) === null) return;
      const open = resource === undefined ? null : validAppResource(resource);
      post('AI_APP_ROUTE', { generation, path, ...(open === null ? {} : { resource: open }) });
    },
    grants(reason) {
      if (pending) return Promise.reject(new Error('busy'));
      if (!trusted) {
        state.set('refused:unavailable');
        return Promise.reject(new Error('unavailable'));
      }
      if (reason === 'open' && initialGrant) {
        const grant = initialGrant;
        initialGrant = undefined;
        state.set('ready');
        return Promise.resolve(grant);
      }
      if (reason === 'open' && initialRefusal) {
        const refused = initialRefusal;
        initialRefusal = undefined;
        state.set(`refused:${refused}`);
        return Promise.reject(new Error(refused));
      }
      if (reason === 'renew' && generation === undefined) {
        state.set('refused:unavailable');
        return Promise.reject(new Error('unavailable'));
      }
      // A renew cannot spend a late INIT. It will request a fresh grant once generation is known.
      if (reason === 'renew') {
        initialGrant = undefined;
        initialRefusal = undefined;
      }
      state.set('waiting_init');
      return new Promise<string>((resolve, reject) => {
        pending = {
          resolve,
          reject,
          retries: 0,
          timer: setTimeout(() => finish(undefined, 'timeout'), 15000),
        };
        if (generation === undefined) {
          // READY already runs since the install; only a spent or stopped schedule is started again (a person's retry)
          if (readyTimers.length === 0) ready();
        } else {
          pending.n = ++n;
          post('AI_AUTH_REQUEST_REFRESH', { requestId: n, generation });
        }
      });
    },
  };
}
